import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useBooking } from '../context/BookingContext';
import { getCityName } from '../data/routes';
import { ArrowLeftRight } from 'lucide-react';
import SearchForm from '../components/SearchForm';

const routesByCountry = {
  estonia: {
    flagCode: 'ee',
    routes: [
      ['tallinn', 'tartu'], ['tallinn', 'narva'], ['tallinn', 'parnu'],
      ['tallinn', 'viljandi'], ['tallinn', 'rakvere'], ['tallinn', 'kuressaare'],
      ['tartu', 'narva'], ['tartu', 'parnu'], ['tartu', 'viljandi'],
      ['parnu', 'riga'], ['tallinn', 'riga'], ['tallinn', 'vilnius'],
      ['tallinn', 'warsaw'], ['tallinn', 'spb'], ['tallinn', 'helsinki'],
      ['rakvere', 'narva'],
    ]
  },
  latvia: {
    flagCode: 'lv',
    routes: [
      ['riga', 'tallinn'], ['riga', 'vilnius'], ['riga', 'kaunas'],
      ['riga', 'daugavpils'], ['riga', 'liepaja'], ['riga', 'parnu'],
      ['riga', 'warsaw'], ['riga', 'spb'], ['riga', 'panevezys'],
      ['riga', 'klaipeda'], ['riga', 'palanga'], ['riga', 'tartu'],
      ['liepaja', 'klaipeda'], ['liepaja', 'palanga'],
    ]
  },
  lithuania: {
    flagCode: 'lt',
    routes: [
      ['vilnius', 'riga'], ['vilnius', 'kaunas'], ['vilnius', 'warsaw'],
      ['vilnius', 'tallinn'], ['vilnius', 'panevezys'], ['vilnius', 'klaipeda'],
      ['vilnius', 'suwalki'], ['kaunas', 'riga'], ['kaunas', 'warsaw'],
      ['kaunas', 'klaipeda'], ['kaunas', 'panevezys'], ['kaunas', 'suwalki'],
      ['panevezys', 'riga'], ['panevezys', 'tallinn'], ['panevezys', 'warsaw'],
      ['klaipeda', 'riga'],
    ]
  },
  russia: {
    flagCode: 'ru',
    routes: [
      ['spb', 'tallinn'], ['spb', 'riga'], ['spb', 'helsinki'],
    ]
  },
  poland: {
    flagCode: 'pl',
    routes: [
      ['warsaw', 'vilnius'], ['warsaw', 'kaunas'], ['warsaw', 'riga'],
      ['warsaw', 'tallinn'], ['warsaw', 'panevezys'],
      ['suwalki', 'vilnius'], ['suwalki', 'kaunas'], ['suwalki', 'warsaw'],
    ]
  },
};

const countryLabels = {
  estonia: { ru: 'Эстония', en: 'Estonia', lv: 'Igaunija', lt: 'Estija', pl: 'Estonia' },
  latvia: { ru: 'Латвия', en: 'Latvia', lv: 'Latvija', lt: 'Latvija', pl: 'Łotwa' },
  lithuania: { ru: 'Литва', en: 'Lithuania', lv: 'Lietuva', lt: 'Lietuva', pl: 'Litwa' },
  russia: { ru: 'Россия', en: 'Russia', lv: 'Krievija', lt: 'Rusija', pl: 'Rosja' },
  poland: { ru: 'Польша', en: 'Poland', lv: 'Polija', lt: 'Lenkija', pl: 'Polska' },
};

const RoutesPage = () => {
  const { lang } = useLanguage();
  const { setSearchParams } = useBooking();
  const navigate = useNavigate();

  const handleRouteClick = (from, to) => {
    const bs = lang === 'ru' ? 'Автовокзал' : lang === 'en' ? 'Bus station' : lang === 'lv' ? 'Autoosta' : lang === 'lt' ? 'Autobusų stotis' : 'Dworzec autobusowy';
    const fromName = `${getCityName(from, lang)}, ${bs}`;
    const toName = `${getCityName(to, lang)}, ${bs}`;
    const today = new Date().toISOString().split('T')[0];
    setSearchParams(p => ({ ...p, from: fromName, to: toName, departureDate: today, _ts: Date.now() }));
    navigate('/search');
  };

  const title = { ru: 'Все маршруты', en: 'All routes', lv: 'Visi maršruti', lt: 'Visi maršrutai', pl: 'Wszystkie trasy' };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="relative bg-[#1a1a2e] min-h-[260px] md:min-h-[300px] flex flex-col items-center justify-center">
        <div className="absolute inset-0" style={{
          backgroundImage: 'url(https://i.ibb.co/DfskPfK7/n-Bk-SUh-L2h-Fkhms2z-Ib6-Br-NOp2-Z3z8-Zj21i-DEh-f-H-n-KUPXua-Dy-XTj-Hou4-MVO6-BCVo-ZKf9-Gq-Ve5-Q-CPawk214-Ly.jpg)',
          backgroundSize: 'cover', backgroundPosition: 'center'
        }} />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 w-full max-w-[1200px] mx-auto px-4 pt-24 md:pt-28 pb-6">
          <h1 className="text-3xl md:text-[40px] font-bold text-white italic mb-6">
            {title[lang] || title.en}
          </h1>
          <SearchForm />
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#c30909]" />
      </div>

      {/* Routes grid */}
      <div className="max-w-[1200px] mx-auto px-4 py-12">
        {Object.entries(routesByCountry).map(([countryId, { flagCode, routes }]) => (
          <div key={countryId} className="mb-12">
            <h2 className="text-xl font-bold text-[#484c55] mb-5 flex items-center gap-3">
              <img
                src={`https://flagcdn.com/w40/${flagCode}.png`}
                srcSet={`https://flagcdn.com/w80/${flagCode}.png 2x`}
                alt={countryLabels[countryId][lang]}
                className="w-7 h-5 object-cover rounded-sm shadow-sm"
              />
              {countryLabels[countryId][lang] || countryLabels[countryId].en}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-0.5">
              {routes.map(([from, to]) => (
                <button
                  key={`${from}-${to}`}
                  onClick={() => handleRouteClick(from, to)}
                  className="flex items-center gap-2 py-2 text-sm text-[#484c55] hover:text-[#c30909] transition-colors text-left group"
                >
                  <span className="font-medium">{getCityName(from, lang)}</span>
                  <ArrowLeftRight size={12} className="text-[#484c55]/30 group-hover:text-[#c30909]/50 flex-shrink-0" />
                  <span className="font-medium">{getCityName(to, lang)}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RoutesPage;
